import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { dokterService, tenantApi } from "../../services/api";

function CareLineDoctors({ slug }) {
    const [doctors, setDoctors] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        fetchDoctors();
    }, [slug]);

    const fetchDoctors = async () => {
        try {
            const response = slug
                ? await tenantApi(slug).dokter.getAll()
                : await dokterService.getAll();
            if (response.data.success) setDoctors(response.data.data);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const getInitials = (name = "") =>
        name
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((w) => w[0].toUpperCase())
            .join("");

    return (
        <section
            id="dokter-section"
            className="relative overflow-hidden py-16 md:py-20 font-[Plus_Jakarta_Sans,sans-serif]"
        >
            <div className="relative z-10 mx-auto max-w-[1280px] px-4 sm:px-6 md:px-10 lg:px-16 xl:px-[60px]">
                <motion.div
                    className="flex flex-col items-center text-center mb-10 md:mb-14"
                    initial={{ opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{
                        duration: 0.7,
                        ease: [0.25, 0.46, 0.45, 0.94],
                    }}
                >
                    <div className="inline-flex items-center gap-2 bg-cyan-50 border border-cyan-200/70 text-cyan-600 text-[11px] font-bold px-3.5 py-1.5 rounded-full uppercase tracking-[0.08em] mb-5">
                        <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full" />
                        Tim Medis
                    </div>
                    <h2 className="text-[clamp(24px,3.5vw,40px)] font-extrabold text-gray-900 leading-tight tracking-[-0.02em] mb-3">
                        Dokter{" "}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#06B6D4] to-[#0891B2]">
                            Berpengalaman
                        </span>{" "}
                        Kami
                    </h2>
                    <p className="text-gray-400 text-[15px] max-w-sm leading-relaxed font-medium">
                        Didukung oleh tenaga medis profesional dan terpercaya
                        untuk kesehatan Anda.
                    </p>
                </motion.div>

                {isLoading ? (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                        {[...Array(3)].map((_, i) => (
                            <div
                                key={i}
                                className="bg-white rounded-[20px] border border-[#e8f4f8] overflow-hidden animate-pulse"
                            >
                                <div className="h-[200px] bg-gray-100" />
                                <div className="p-5 space-y-3">
                                    <div className="h-3 bg-gray-100 rounded-full w-1/3" />
                                    <div className="h-4 bg-gray-100 rounded-full w-3/4" />
                                    <div className="h-3 bg-gray-100 rounded-full w-full" />
                                    <div className="h-9 bg-gray-100 rounded-xl mt-4" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : doctors.length > 0 ? (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                        {doctors.map((doc, idx) => (
                            <motion.div
                                key={doc.id_dokter}
                                className="group relative bg-white rounded-[20px] border border-[#e8f4f8] overflow-hidden flex flex-col"
                                initial={{ opacity: 0, y: 36 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.15 }}
                                transition={{
                                    duration: 0.6,
                                    delay: idx * 0.1,
                                    ease: [0.25, 0.46, 0.45, 0.94],
                                }}
                                whileHover={{
                                    y: -6,
                                    boxShadow:
                                        "0 8px 16px rgba(0,0,0,0.05), 0 24px 48px rgba(6,182,212,0.10)",
                                    borderColor: "rgba(6,182,212,0.2)",
                                }}
                            >
                                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#06B6D4] to-[#67e8f9] rounded-t-[20px] scale-x-0 origin-left transition-transform duration-[350ms] group-hover:scale-x-100 z-10" />
                                <div className="h-[200px] overflow-hidden flex-shrink-0 bg-gradient-to-br from-[#e0f7fa] to-[#b2ebf2] flex items-center justify-center">
                                    {doc.foto ? (
                                        <img
                                            src={doc.foto}
                                            alt={doc.nama_dokter}
                                            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                                            onError={(e) => {
                                                e.target.style.display = "none";
                                                e.target.nextSibling.style.display =
                                                    "flex";
                                            }}
                                        />
                                    ) : null}
                                    <div
                                        className="w-20 h-20 rounded-full bg-gradient-to-br from-[#06B6D4] to-[#0891B2] flex items-center justify-center text-white text-2xl font-extrabold"
                                        style={{
                                            display: doc.foto ? "none" : "flex",
                                        }}
                                    >
                                        {getInitials(doc.nama_dokter)}
                                    </div>
                                </div>
                                <div className="flex flex-col flex-1 p-5">
                                    <div className="inline-flex items-center gap-1.5 self-start bg-cyan-50 border border-cyan-200/60 text-cyan-700 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-[0.05em] mb-2.5">
                                        <span className="w-1 h-1 bg-cyan-500 rounded-full" />
                                        {doc.poli?.nama_poli || "Dokter Umum"}
                                    </div>
                                    <h3 className="text-[15px] font-extrabold text-gray-900 leading-snug tracking-[-0.01em] mb-2">
                                        {doc.nama_dokter}
                                    </h3>
                                    <p className="text-gray-400 text-[12.5px] font-medium leading-relaxed mb-3 flex-1 line-clamp-2">
                                        {doc.deskripsi ||
                                            "Profesional medis yang berdedikasi untuk memberikan pelayanan kesehatan terbaik."}
                                    </p>
                                    <div className="w-full h-px bg-gradient-to-r from-cyan-100 to-transparent mb-3" />
                                    <div className="flex items-center gap-1.5 mb-3">
                                        <div className="w-5 h-[3px] rounded-full bg-[#06B6D4]" />
                                        <div
                                            className={`w-2 h-[3px] rounded-full ${idx % 3 > 0 ? "bg-cyan-200" : "bg-gray-100"}`}
                                        />
                                        <div className="w-2 h-[3px] rounded-full bg-gray-100" />
                                    </div>
                                    <button className="w-full py-2.5 rounded-xl bg-gradient-to-r from-[#06B6D4] to-[#0891B2] text-white text-[12.5px] font-bold transition-opacity duration-200 hover:opacity-90">
                                        Lihat Jadwal
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                        <p className="text-gray-400 text-[16px] font-medium">
                            Belum ada dokter terdaftar.
                        </p>
                    </div>
                )}
            </div>
        </section>
    );
}

export default CareLineDoctors;